'use client';

import Script from 'next/script';
import { Suspense, useEffect, useRef, useSyncExternalStore } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import {
  initNaverPremiumLog,
  sendNaverPageView,
  trackNaverSignUp,
  trackNaverPurchase,
  hasNaverRegistrationSent,
  markNaverRegistrationSent,
  hasNaverPurchaseSent,
  markNaverPurchaseSent,
} from '@/lib/naverPremiumLog';

const SIGN_UP_INTENT_KEY = 'naver_log_signup_intent';

// 네이버 공통키
const NAVER_ACCOUNT_ID =
  process.env.NEXT_PUBLIC_NAVER_PREMIUM_LOG_ID || 's_2cb9e37abe77';

const subscribeNoop = () => () => {};
const getClientMountedSnapshot = () => true;
const getServerMountedSnapshot = () => false;

/**
 * 페이지뷰 및 전환 추적을 담당하는 내부 컴포넌트
 * useSearchParams를 사용하므로 Suspense로 감싸야 함
 */
function NaverPremiumLogTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isSignedIn, user, isLoaded } = useUser();
  const hasTrackedRegistration = useRef(false);
  const hasTrackedPurchase = useRef(false);
  const previousPathRef = useRef<string | null>(null);

  // SPA 라우트 변경 감지 및 PV 이벤트 전송
  useEffect(() => {
    // 이전 경로와 같으면 무시
    if (previousPathRef.current === pathname) return;

    // 첫 렌더링은 공통 스크립트에서 wcs_do()를 호출하므로 무시
    if (previousPathRef.current === null) {
      previousPathRef.current = pathname;
      return;
    }

    previousPathRef.current = pathname;

    // SPA 라우트 변경 시 PV 이벤트 전송
    if (process.env.NODE_ENV === 'development') {
      console.log('[Naver Premium Log] SPA PV sent:', pathname);
    }
    sendNaverPageView();
  }, [pathname]);

  // /sign-up 페이지 방문 시 가입 의도 플래그 세팅
  useEffect(() => {
    try {
      if (pathname?.startsWith('/sign-up') || pathname?.startsWith('/sign-in')) {
        sessionStorage.setItem(SIGN_UP_INTENT_KEY, 'true');
        if (process.env.NODE_ENV === 'development') {
          console.log('[Naver Premium Log] Sign-up intent flagged');
        }
      }
    } catch {
      // sessionStorage 접근 불가 시 무시
    }
  }, [pathname]);

  // 회원가입 완료 감지 및 이벤트 전송
  useEffect(() => {
    if (!isLoaded || hasTrackedRegistration.current) return;

    try {
      if (isSignedIn && user) {
        const hadSignUpIntent = sessionStorage.getItem(SIGN_UP_INTENT_KEY) === 'true';
        const createdAt = user.createdAt;
        const createdTime = createdAt ? new Date(createdAt).getTime() : 0;
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        const isNewUser = createdTime > 0 && now - createdTime < fiveMinutes;

        if (process.env.NODE_ENV === 'development') {
          console.log('[Naver Premium Log] Sign-up check:', {
            hadSignUpIntent,
            isNewUser,
            createdAt,
            timeDiff: createdTime > 0 ? `${Math.round((now - createdTime) / 1000)}s` : 'N/A',
            alreadySent: hasNaverRegistrationSent(),
          });
        }

        if (hadSignUpIntent && isNewUser && !hasNaverRegistrationSent()) {
          trackNaverSignUp({ id: user.id });
          markNaverRegistrationSent();
          hasTrackedRegistration.current = true;
          sessionStorage.removeItem(SIGN_UP_INTENT_KEY);

          if (process.env.NODE_ENV === 'development') {
            console.log('[Naver Premium Log] Sign-up conversion sent:', user.id);
          }
        }
      }
    } catch {
      // sessionStorage/localStorage 접근 불가 시 무시
    }
  }, [isLoaded, isSignedIn, user]);

  // 결제 성공 감지 및 구매 전환 이벤트 전송
  useEffect(() => {
    if (hasTrackedPurchase.current) return;

    const success = searchParams.get('success');
    const orderId = searchParams.get('orderId');
    const amount = searchParams.get('amount');
    const productId = searchParams.get('productId');
    const productName = searchParams.get('productName');

    // 결제 성공 파라미터가 모두 있는 경우
    if (success === 'true' && orderId && amount && productId && productName) {
      // 이미 전송된 주문인지 확인
      if (!hasNaverPurchaseSent(orderId)) {
        trackNaverPurchase({
          orderId,
          productId: decodeURIComponent(productId),
          productName: decodeURIComponent(productName),
          amount: Number(amount),
        });
        markNaverPurchaseSent(orderId);
        hasTrackedPurchase.current = true;

        if (process.env.NODE_ENV === 'development') {
          console.log('[Naver Premium Log] Purchase tracked:', {
            orderId,
            productId,
            productName,
            amount,
          });
        }
      }
    }
  }, [searchParams]);

  return null;
}

function MountedNaverPremiumLogTracker() {
  const mounted = useSyncExternalStore(
    subscribeNoop,
    getClientMountedSnapshot,
    getServerMountedSnapshot,
  );

  if (!mounted) {
    return null;
  }

  return <NaverPremiumLogTracker />;
}

export default function NaverPremiumLog() {
  // 환경 변수가 없으면 렌더링하지 않음
  if (!NAVER_ACCOUNT_ID) {
    return null;
  }

  return (
    <>
      {/* 네이버 프리미엄 로그 공통 스크립트.
          afterInteractive로 로드하여 next/script가 네이티브 <script> 대신 imperative하게
          삽입하도록 한다(React 19의 raw <script> 렌더 경고 회피). 로드 완료(onLoad) 시점에
          wcs 전역이 준비된 뒤 초기화 + 초기 PV를 전송한다. */}
      <Script
        id="naver-wcslog"
        src="//wcs.naver.net/wcslog.js"
        strategy="afterInteractive"
        onLoad={() => initNaverPremiumLog(NAVER_ACCOUNT_ID)}
      />
      {/* SPA 라우트 변경 및 전환 추적 컴포넌트 */}
      <Suspense fallback={null}>
        <MountedNaverPremiumLogTracker />
      </Suspense>
    </>
  );
}
